import React, { useState, useRef, useLayoutEffect } from 'react';
import classNames from 'classnames';
import { SuggestionDataItem } from 'react-mentions';
import { scroller } from 'react-scroll';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import CommentBodyRenderer from 'buyside/components/presentation/CommentBodyRenderer/commentBodyRenderer';
import CommentAttachments from 'buyside/components/presentation/Comment/CommentAttachments/commentAttachments';
import CommentActions from 'buyside/components/presentation/Comment/CommentActions/commentActions';
import CommentHeader from 'buyside/components/presentation/Comment/CommentHeader/commentHeader';
import IdeaUpdateCommentForm from 'buyside/components/presentation/IdeaUpdates/IdeaUpdateCommentForm/ideaUpdateCommentForm';

import {
  IdeaShowPageCommentFragment,
  Maybe,
  useLikeCommentMutation
} from 'interfaces/graphql-typings';

import styles from 'buyside/components/presentation/IdeaUpdates/IdeaUpdateComments/ideaUpdateCommentsStyles.scss';

const IdeaUpdateComment: React.FC<{
  allChildComments: Maybe<IdeaShowPageCommentFragment>[];
  authorId: string;
  authorName: Maybe<string>;
  canViewUserDetails: boolean;
  canWriteComments: boolean;
  comment: IdeaShowPageCommentFragment;
  isAdmin: boolean;
  isAuthor: boolean;
  ideaUpdateId: string;
  canCommentAnonymously: boolean;
  mentionables: SuggestionDataItem[];
  isAllocator: boolean;
  highlightedCommentId?: string;
  isIdeaAnonymous: boolean;
  isNonBuyside: boolean;
  currentUserId: string;
}> = ({
  allChildComments,
  authorId,
  authorName,
  canCommentAnonymously,
  canViewUserDetails,
  canWriteComments,
  comment,
  highlightedCommentId,
  ideaUpdateId,
  isAdmin,
  isAllocator,
  isAuthor,
  mentionables,
  isIdeaAnonymous,
  isNonBuyside,
  currentUserId
}): React.ReactElement => {
  const {
    anonUser,
    body,
    commentAttachments,
    createdAt,
    currentUserHasLiked,
    id: commentId,
    likeCount,
    responseRequested,
    user
  } = comment;

  const [showTruncation, setShowTruncation] = useState(true);
  const [truncated, setTruncated] = useState(true);
  const [showChildComments, setShowChildComments] = useState(true);
  const [showReply, setShowReply] = useState(false);

  const toggleReply = (): void => setShowReply(!showReply);
  const toggleShowChildComments = (): void => setShowChildComments(!showChildComments);

  const parentDivRef = useRef<HTMLDivElement>(null);
  const id = `update-comment-${commentId}-top`;

  const isAuthorComment = authorId === user?.id;
  const showUserInfo =
    canViewUserDetails || currentUserId === user?.id || (isNonBuyside && isAuthorComment);

  const childComments = allChildComments?.filter(c => c && c?.parentId === comment?.id);

  const [likeComment, { loading: likeLoading }] = useLikeCommentMutation({
    variables: { commentId },
    update(cache, { data }) {
      if (!data?.likeComment?.comment) return;
      const hasLiked = Boolean(data.likeComment.comment.currentUserHasLiked);

      cache.modify({
        id: cache.identify({ id: data.likeComment.comment.id, __typename: 'Comment' }),
        fields: { currentUserHasLiked: () => hasLiked }
      });
    }
  });

  const toggleReadMoreLess = (): void => {
    if (!truncated) {
      scroller.scrollTo(id, {
        duration: 500,
        delay: 0,
        smooth: 'easeInOutQuint',
        offset: -(65 + 45 + 15) // -(top navigation height + page navigator height + some padding)
      });
    }
    setTruncated(!truncated);
  };

  const assessCommentHeight = (element: any): void => {
    if (element && element.offsetHeight < 100) setShowTruncation(false);
  };

  useLayoutEffect(() => {
    if (Boolean(highlightedCommentId === comment?.id) && parentDivRef.current) {
      parentDivRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  });

  return (
    <div
      className={classNames(styles.commentContainer, {
        [styles.childComment]: Boolean(comment?.parentId)
      })}
    >
      <div className={styles.comment} id={id} ref={parentDivRef}>
        <CommentHeader
          anonUser={anonUser}
          authorName={authorName}
          createdAt={createdAt}
          isAdmin={isAdmin}
          isAuthor={isAuthor}
          isAuthorComment={isAuthorComment}
          isNonBuyside={isNonBuyside}
          responseRequested={Boolean(responseRequested)}
          showUserInfo={showUserInfo}
          user={user}
        />
        <div className={styles.commentBody}>
          <div
            className={classNames(styles.unselectable, { [styles.truncated]: truncated })}
            ref={assessCommentHeight}
          >
            <CommentBodyRenderer body={body} />
          </div>
          {showTruncation && (
            <div className={styles.truncateButton} onClick={toggleReadMoreLess}>
              {truncated ? 'Read More' : 'Read Less'}
            </div>
          )}
        </div>
        <CommentAttachments commentAttachments={commentAttachments} />
        <CommentActions
          canWriteComments={canWriteComments}
          commentId={comment?.id}
          currentUserHasLiked={currentUserHasLiked}
          hasChildComments={Boolean(childComments?.length)}
          likeComment={likeComment}
          likeCount={likeCount}
          likeLoading={likeLoading}
          showChildComments={showChildComments}
          toggleReply={toggleReply}
          toggleShowChildComments={toggleShowChildComments}
        />
        {showReply && (
          <IdeaUpdateCommentForm
            canCommentAnonymously={canCommentAnonymously}
            ideaUpdateId={ideaUpdateId}
            isAllocator={isAllocator}
            isAuthor={isAuthor}
            isIdeaAnonymous={isIdeaAnonymous}
            isReplying
            mentionables={mentionables}
            parentId={comment.id}
          />
        )}
      </div>
      <div className={styles.childComments}>
        {showChildComments && (
          <TransitionGroup>
            {childComments?.map(childComment => {
              return childComment ? (
                <CSSTransition
                  key={`idea-update-comment-${childComment?.id}`}
                  classNames="fade"
                  timeout={500}
                  in
                  appear
                >
                  <IdeaUpdateComment
                    allChildComments={allChildComments}
                    authorId={authorId}
                    authorName={authorName}
                    canCommentAnonymously={canCommentAnonymously}
                    canViewUserDetails={canViewUserDetails}
                    canWriteComments={canWriteComments}
                    comment={childComment}
                    currentUserId={currentUserId}
                    highlightedCommentId={highlightedCommentId}
                    ideaUpdateId={ideaUpdateId}
                    isAdmin={isAdmin}
                    isAllocator={isAllocator}
                    isAuthor={isAuthor}
                    isIdeaAnonymous={isIdeaAnonymous}
                    isNonBuyside={isNonBuyside}
                    key={childComment?.id}
                    mentionables={mentionables}
                  />
                </CSSTransition>
              ) : null;
            })}
          </TransitionGroup>
        )}
      </div>
    </div>
  );
};

export default IdeaUpdateComment;
